import React, { useEffect, useMemo, useState } from 'react';

function formatDuration(msRemaining: number): string {
  if (msRemaining <= 0) return '00:00:00';
  const totalSeconds = Math.floor(msRemaining / 1000);
  const hours = Math.floor(totalSeconds / 3600)
    .toString()
    .padStart(2, '0');
  const minutes = Math.floor((totalSeconds % 3600) / 60)
    .toString()
    .padStart(2, '0');
  const seconds = Math.floor(totalSeconds % 60)
    .toString()
    .padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

type Props = {
  start?: Date;
  end?: Date;
  startLabel?: string;
  endLabel?: string;
};

export default function DowntimeScreen({ start, end, startLabel, endLabel }: Props) {
  const now = useMemo(() => new Date(), []);

  // Maintenance window: from the start of the next hour, lasting 2 hours
  const maintenanceStart = useMemo(() => {
    if (start) return start;
    const s = new Date(now);
    s.setMinutes(0, 0, 0);
    s.setHours(s.getHours() + 1);
    return s;
  }, [now, start]);

  const maintenanceEnd = useMemo(() => {
    if (end) return end;
    const e = new Date(maintenanceStart);
    e.setHours(e.getHours() + 2);
    return e;
  }, [maintenanceStart, end]);

  const [nowTs, setNowTs] = useState<number>(Date.now());

  useEffect(() => {
    const intervalId = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, []);

  const timeRemainingMs = Math.max(0, maintenanceEnd.getTime() - nowTs);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <span style={styles.title}>Scheduled Maintenance</span>
        </div>
        <p style={styles.subtitle}>
          The dashboard is temporarily unavailable for scheduled maintenance.
        </p>
        <div style={styles.windowBox}>
          <div style={styles.windowRow}><strong>Start:</strong> {startLabel ?? maintenanceStart.toLocaleString()}</div>
          <div style={styles.windowRow}><strong>End:</strong> {endLabel ?? maintenanceEnd.toLocaleString()}</div>
        </div>
        <div style={styles.countdownLabel}>Time remaining</div>
        <div style={styles.countdown}>{formatDuration(timeRemainingMs)}</div>
        <p style={styles.helpText}>We'll be back automatically when the window ends.</p>
      </div>
    </div>
  );
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #0f172a 0%, #111827 50%, #0b1220 100%)',
    color: '#e5e7eb',
    zIndex: 9999,
  },
  card: {
    width: 'min(92vw, 720px)',
    padding: '32px',
    borderRadius: 16,
    backgroundColor: 'rgba(17, 24, 39, 0.85)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
    textAlign: 'center',
    backdropFilter: 'blur(6px)',
  },
  logoRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    marginBottom: 8,
  },
  title: {
    fontSize: 28,
    fontWeight: 700,
    letterSpacing: 0.2,
    color: '#f9fafb',
  },
  subtitle: {
    marginTop: 4,
    marginBottom: 20,
    color: '#cbd5e1',
    fontSize: 16,
  },
  windowBox: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 6,
    padding: '12px 14px',
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.06)',
    marginBottom: 18,
    color: '#e5e7eb',
  },
  windowRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    fontSize: 14,
  },
  countdownLabel: {
    fontSize: 13,
    color: '#a5b4fc',
    letterSpacing: 1,
    textTransform: 'uppercase',
    marginTop: 6,
    marginBottom: 4,
  },
  countdown: {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    fontSize: 40,
    fontWeight: 800,
    color: '#fbbf24',
    textShadow: '0 1px 0 rgba(0,0,0,0.5)',
    letterSpacing: 1,
    marginBottom: 8,
  },
  helpText: {
    fontSize: 13,
    color: '#9ca3af',
    marginTop: 8,
  },
};


